import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { COUNTRIES } from "../data/site";
import { PRODUCTS, CATEGORIES } from "../data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-black/15 bg-white px-4 py-3 text-sm text-[#0E1526] placeholder:text-slate-400 outline-none transition-colors focus:border-amber-500";
const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400";

export const InquiryForm = ({ defaultProduct = "", compact = false }) => {
  const [params] = useSearchParams();
  const prefill = defaultProduct || params.get("product") || "";
  const [form, setForm] = useState({
    name: "",
    email: "",
    country_code: "+971",
    phone: "",
    product: prefill,
    message: "",
    consent: false,
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please accept the privacy consent to submit.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Submission failed");
      }
      setSent(true);
      toast.success("Inquiry received. Our trading desk will reply within one business day.");
    } catch (err) {
      toast.error(err.message || "Could not submit. Please try WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="inquiry-success" className="border border-amber-500/40 bg-white p-10 text-center">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500">Inquiry logged</p>
        <h3 className="mt-4 font-display text-3xl font-semibold text-[#0E1526]">Thank you, {form.name.split(" ")[0]}.</h3>
        <p className="mt-3 text-sm leading-relaxed text-slate-400">
          Your inquiry has been recorded and routed to our trading desk. Expect a response at{" "}
          <span className="text-slate-700">{form.email}</span> within one business day.
        </p>
        <button
          data-testid="inquiry-another-button"
          onClick={() => { setSent(false); setForm((f) => ({ ...f, message: "" })); }}
          className="mt-6 border border-black/20 px-6 py-2.5 text-sm text-slate-700 transition-colors hover:border-amber-500 hover:text-[#0E1526]"
        >
          Send another inquiry
        </button>
      </div>
    );
  }

  return (
    <form data-testid="inquiry-form" onSubmit={submit} className="space-y-5">
      <div className={`grid gap-5 ${compact ? "" : "sm:grid-cols-2"}`}>
        <div>
          <label htmlFor="inq-name" className={labelCls}>Full name *</label>
          <input id="inq-name" data-testid="inquiry-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Jane Mensah" className={inputCls} />
        </div>
        <div>
          <label htmlFor="inq-email" className={labelCls}>Business email *</label>
          <input id="inq-email" data-testid="inquiry-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="jane@company.com" className={inputCls} />
        </div>
      </div>
      <div className={`grid gap-5 ${compact ? "" : "sm:grid-cols-2"}`}>
        <div>
          <label htmlFor="inq-phone" className={labelCls}>Phone / WhatsApp *</label>
          <div className="flex">
            <select
              data-testid="inquiry-country-code-select"
              value={form.country_code}
              onChange={set("country_code")}
              aria-label="Country code"
              className={`${inputCls} w-32 shrink-0 appearance-none border-r-0`}
            >
              {COUNTRIES.map(([c, code], i) => (
                <option key={`${code}-${i}`} value={code} className="bg-white">
                  {code} {c}
                </option>
              ))}
            </select>
            <input id="inq-phone" data-testid="inquiry-phone-input" required minLength={5} value={form.phone} onChange={set("phone")} placeholder="50 123 4567" className={inputCls} />
          </div>
        </div>
        <div>
          <label htmlFor="inq-product" className={labelCls}>Product of interest</label>
          <select
            id="inq-product"
            data-testid="inquiry-product-select"
            value={form.product}
            onChange={set("product")}
            className={`${inputCls} appearance-none`}
          >
            <option value="" className="bg-white">General inquiry</option>
            {CATEGORIES.map((c) => (
              <optgroup key={c.id} label={c.label}>
                {PRODUCTS.filter((p) => p.category === c.id).map((p) => (
                  <option key={p.slug} value={p.name} className="bg-white">{p.name}</option>
                ))}
              </optgroup>
            ))}
            <option value="Steel products" className="bg-white">Steel products</option>
            <option value="Other / custom requirement" className="bg-white">Other / custom requirement</option>
          </select>
        </div>
      </div>
      <div>
        <label htmlFor="inq-message" className={labelCls}>Message *</label>
        <textarea
          id="inq-message"
          data-testid="inquiry-message-input"
          required
          minLength={10}
          rows={compact ? 4 : 5}
          value={form.message}
          onChange={set("message")}
          placeholder="Required grade, quantity, destination port, target timeline…"
          className={`${inputCls} resize-y`}
        />
      </div>
      <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-slate-600">
        <input
          data-testid="inquiry-consent-checkbox"
          type="checkbox"
          checked={form.consent}
          onChange={set("consent")}
          className="mt-0.5 h-4 w-4 shrink-0 accent-amber-500"
        />
        <span>
          I consent to {`Terra Bloom DWC LLC`} storing and processing my details to respond to this inquiry, as
          described in the <a href="/privacy" data-testid="inquiry-privacy-link" className="text-amber-500 underline underline-offset-2">Privacy Policy</a>.
        </span>
      </label>
      <button
        data-testid="inquiry-submit-button"
        type="submit"
        disabled={sending}
        className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-amber-500 px-8 py-3.5 text-sm font-semibold text-[#0A101D] transition-colors hover:bg-amber-400 disabled:opacity-60"
      >
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />}
        {sending ? "Sending…" : "Submit Inquiry"}
      </button>
    </form>
  );
};
